/**
 * Swatch Redirect Module
 * 
 * This module handles automatic redirection for swatch product pages. Swatches are low-cost
 * sample products that are linked to main product variants. When a user visits a swatch 
 * product page, this script automatically redirects them to the original product page with 
 * the associated variant pre-selected.
 * 
 * The script waits for the product data to be loaded (from product-data.js) and then
 * searches through all products to find the variant that has a swatch ID matching
 * the current swatch product. Once found, it redirects the user to that variant's URL.
 * 
 * @author Pepper Lee Theme
 * @requires window.productData - Global product data array loaded by product-data.js
 */

/**
 * Redirects users from a swatch product page to the original product variant page
 * 
 * This function performs the following steps:
 * 1. Waits for the global product data to be available
 * 2. Searches through all products and variants to find a match
 * 3. Redirects to the matching variant's URL
 * 
 * @param {string} swatch - The swatch ID to search for (typically the current swatch product's variant ID)
 * @returns {Promise<void>} Promise that resolves when the redirect is initiated
 * 
 * @example
 * // Called from theme.liquid when viewing a swatch product
 * swatchRedirect('123456789');
 */
async function swatchRedirect(swatch) {
  // Wait for the global product data to be loaded by product-data.js
  // This ensures we have access to all product and variant information
  await new Promise(
    (resolve) => {
      // Check every 100ms if the product data is available
      const interval = setInterval(
        () => {
          if (typeof window.productData == 'undefined') {
            return; // Continue waiting if product data isn't loaded yet
          }
          clearInterval(interval); // Stop checking once data is available
          resolve(); // Resolve the promise to continue execution
        },
        100 // Check every 100ms for optimal responsiveness
      );
    }
  );

  // Search through all products to find the variant with a matching swatch ID
  for (const product of window.productData) {
    // Check each variant within the current product
    for (const variant of product.variants) {
      // If this variant's swatch ID doesn't match our target, skip to the next variant
      if (variant.swatch != swatch) {
        continue;
      }
      
      // Found a matching variant! Redirect the user to that variant's product page
      // This will take them to the main product with the correct variant pre-selected
      window.location.href = variant.url;
      return; // Exit the function after initiating the redirect
    }
  }
  
  // Note: If no matching variant is found, the user stays on the current swatch page
  // This could happen if the metafield relationship is broken or the variant was deleted
}